import type { RouteLocationNormalized } from 'vue-router';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { captureMessageForSentry } from '@/libraries/sentry';
import { headerfulCachedRequest } from '@/helpers/request-helper';
import {
	GetUrlMetadataDocument,
	GetUrlMetadataQuery,
	GetUrlMetadataQueryVariables,
} from '@/graphql/query/GetUrlMetadata.query';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { Pinia } from 'pinia';

/**
 * Supported domains/subdomains
 * This is also where we declare what the subdomain name actually is, e.g. guides.justwatch.com or sports.justwatch.com
 */
export const Domain = {
	MAIN: 'www',
	EDITORIAL: 'guides',
	SPORTS: 'sports',
} as const;

export type Domain = (typeof Domain)[keyof typeof Domain];

/**
 * Extracts subdomain from a hostname or site header value
 * @param input - The hostname to parse (e.g., 'guides.justwatch.com') or site header value (e.g., 'guides', 'www')
 * @returns The subdomain part or null if no subdomain
 */
export function getSubdomain(input: string): Domain | null {
	if (!input) return null;

	// Remove port if present (e.g., 'guides.localhost:8080' -> 'guides.localhost')
	const inputWithoutPort = input.split(':')[0];

	// Check if this is a simple site header value (no dots)
	if (!inputWithoutPort.includes('.')) {
		// Direct site header values like 'guides', 'www', 'sports'
		// Return null for 'www' as it represents the main domain
		return inputWithoutPort === 'www' ? null : (inputWithoutPort as Domain);
	}

	// Handle hostname formats with dots
	const parts = inputWithoutPort.split('.');

	// Handle .localhost domains (e.g., 'guides.localhost')
	if (parts.length === 2 && parts[1] === 'localhost') {
		return parts[0] === 'localhost' ? null : (parts[0] as Domain);
	}

	// Handle regular domains (e.g., 'guides.justwatch.com')
	if (parts.length <= 2) {
		return null;
	}

	// Return first part as subdomain, but return null for 'www'
	const subdomain = parts[0];
	return subdomain === 'www' ? null : (subdomain as Domain);
}

/**
 * Detects which domain the current input belongs to
 * @param input - The hostname (e.g., 'guides.justwatch.com', 'guides.localhost:8080') or site header value (e.g., 'guides', 'www') to check
 * @returns The domain type
 */
export function detectDomain(input: string): Domain {
	// Handle empty or invalid input
	if (!input || typeof input !== 'string') return Domain.MAIN;

	const subdomain = getSubdomain(input);

	// Map subdomains to domain types - easily extensible for new subdomains
	if (!subdomain) {
		return Domain.MAIN;
	}

	// Check if the subdomain matches any known domain values
	const domainValues = Object.values(Domain) as Domain[];
	for (const domainValue of domainValues) {
		if (subdomain === domainValue) {
			return domainValue;
		}
	}

	// Default to main domain for unknown subdomains
	return Domain.MAIN;
}

/**
 * Gets the appropriate domain for a given route based on its meta
 */
export function getDomainForRoute(routeMeta: any): Domain {
	if (!routeMeta) return Domain.MAIN;

	// Check if route explicitly specifies a subdomain in its meta
	if (routeMeta.subdomain && routeMeta.subdomain !== Domain.MAIN) {
		return routeMeta.subdomain;
	}

	// Default to main domain for all routes unless explicitly marked
	return Domain.MAIN;
}

/**
 * Get current location info for URL building
 * Works consistently across client and server environments
 */
function getCurrentLocation(): string | { protocol: string; host: string } {
	if (import.meta.client && window.location) {
		// Use window.location for better URL handling on client
		return window.location;
	}

	if (import.meta.server) {
		const requestUrl = useRequestURL();

		return { protocol: requestUrl.protocol, host: requestUrl.host };
	}
	captureMessageForSentry(
		'subdomain-helper: Could not find location on client via window.location or during SSR from Vue context.',
		{
			where: '[subdomain-helper.ts] getCurrentLocation()',
		},
		'warning'
	);

	// Fallback if no location info is available
	return `https://www.${JW_CONFIG.DOMAIN}`;
}

/**
 * Builds the full URL for a route on the appropriate domain
 * Uses URL API for more robust URL handling
 */
export function buildUrlForDomain(path: string, targetDomain: Domain): string {
	// Handle different input types - string or location-like object
	let protocol = 'https:';
	let currentHost = '';

	const currentLocation = getCurrentLocation();

	if (typeof currentLocation === 'string') {
		// If a string is provided, it's assumed to be the hostname or full URL
		if (currentLocation.includes('://')) {
			// It's a full URL, parse it
			try {
				const url = new URL(currentLocation);
				// Force HTTPS for production domains, allow original protocol for localhost
				protocol = url.hostname.includes('localhost') ? url.protocol : 'https:';
				currentHost = url.host; // includes port if present
			} catch (e) {
				// In case parsing fails, fall back to treating it as a hostname
				currentHost = currentLocation;
			}
		} else {
			currentHost = currentLocation;
		}
	} else {
		// It's a location-like object
		// Force HTTPS for production domains, allow original protocol for localhost
		if (currentLocation.host && !currentLocation.host.includes('localhost')) {
			protocol = 'https:';
		} else {
			protocol = currentLocation.protocol || protocol;
		}
		currentHost = currentLocation.host;
	}

	// Get the current subdomain
	const currentSubdomain = getSubdomain(currentHost);

	// Prepare the base URL
	let baseUrl: string;

	// For localhost, handle specially
	if (currentHost.includes('localhost')) {
		// Remove any existing subdomain
		const localhostBase = currentSubdomain ? currentHost.replace(`${currentSubdomain}.`, '') : currentHost;

		// Apply the target subdomain
		const subdomain = targetDomain === Domain.MAIN ? '' : targetDomain;
		baseUrl = `${protocol}//${subdomain ? `${subdomain}.` : ''}${localhostBase}`;
	} else {
		// For production domains, extract the base domain (e.g., justwatch.com)
		const baseDomain = JW_CONFIG.DOMAIN; // e.g., justwatch.com

		// Apply the target subdomain
		const subdomain = targetDomain === Domain.MAIN ? 'www' : targetDomain;
		baseUrl = `${protocol}//${subdomain}.${baseDomain}`;
	}

	// Combine with the path, ensuring proper slash handling
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	return `${baseUrl}${normalizedPath}`;
}

async function isValidPath(targetPath: string, targetDomain: Domain, jwId: string | undefined) {
	try {
		const variables: GetUrlMetadataQueryVariables = { fullPath: targetPath, site: targetDomain };
		const response: { data: GetUrlMetadataQuery } = await headerfulCachedRequest(
			GetUrlMetadataDocument,
			variables,
			jwId
		);
		return !!response?.data?.urlV2;
	} catch (error: any) {
		// If the request fails (network error, 500, etc.), treat the path as invalid
		// This matches the previous behavior where headerfulCachedRequest returned undefined on error
		captureMessageForSentry(
			'[subdomain-helper.ts] isValidPath request failed',
			{
				error,
				targetPath,
				targetDomain,
				where: 'subdomain-helper.ts isValidPath',
			},
			'warning'
		);
		return false;
	}
}

export async function handleSubdomainSwitch(context: { pinia: Pinia }, to: RouteLocationNormalized) {
	const currentDomain = detectDomain(window.location.hostname);
	const targetDomain = getDomainForRoute(to.meta);
	const userStore = useUserStore(context.pinia);
	const languageStore = useLanguageStore(context.pinia);
	const routingStore = useRoutingStore(context.pinia);
	const currentWebLocale = languageStore.webLocale;
	const jwId = userStore.jwId;

	// Check if we need to switch domains
	if (currentDomain === targetDomain) return;

	// Special case: If we're on any subdomain and trying to access the root path (/),
	// and the route has matching subdomain metadata, this should be the subdomain landing page.
	// Don't redirect to main domain.
	if (currentDomain !== Domain.MAIN && to.path === '/' && to.meta?.subdomain === currentDomain) {
		return;
	}

	// Don't redirect when going FROM main domain TO sports/editorial subdomains
	// This allows main domain fallback routes to work for backward compatibility
	// (bookmarks, old links, direct URL access)
	// Only redirect when going FROM sports/editorial TO main, or between subdomains
	if (currentDomain === Domain.MAIN && (targetDomain === Domain.SPORTS || targetDomain === Domain.EDITORIAL)) {
		return;
	}

	// Don't redirect between non-main subdomains (e.g. guides.domain -> sports.domain).
	// This can happen when a locale supports sports but not editorial — the sports landing
	// route (path: '/') would match on guides.domain and incorrectly trigger a redirect.
	if (currentDomain !== Domain.MAIN && targetDomain !== Domain.MAIN) {
		return;
	}

	let targetPath = '';

	// Handle navigation from any subdomain to main domain
	if (targetDomain === Domain.MAIN) {
		// Special handling for different route types
		if (to.name === 'app.home') {
			// Home page should go to root of main domain
			targetPath = '/';
		} else if (to.name === 'app.titles.popular.list') {
			// Popular page goes to locale root
			targetPath = `/${currentWebLocale}`;
		} else {
			// All other routes: ensure they have locale prefix
			targetPath = to.fullPath.startsWith(`/${currentWebLocale}`)
				? to.fullPath
				: `/${currentWebLocale}${to.fullPath}`;
		}
	}
	// Handle navigation from main domain or other subdomains to target subdomain
	else {
		targetPath = to.fullPath;
	}

	const shouldRedirect = await isValidPath(targetPath, targetDomain, jwId);

	if (!shouldRedirect) {
		// If the target path is not valid on the target domain, do not redirect
		captureMessageForSentry(
			'Path Not found on Target Domain',
			{
				targetPath,
				targetDomain,
				where: 'subdomain-helper.ts',
			},
			'info'
		);
		return;
	}

	routingStore.setActiveDomain(targetDomain);

	// Perform the domain switch
	const targetUrl = buildUrlForDomain(targetPath, targetDomain);
	window.location.href = targetUrl;
	return; // Don't call next() since we're redirecting
}
